import type Phaser from 'phaser';
import { TILE_SIZE } from '../layout';
import { PICKUP_FRAME, PICKUPS_KEY } from './placeholderArt';

/** How high the star floats over its tile, and how far it bobs, in pixels. */
const FLOAT = 7;
const BOB = 2;
const BOB_MS = 900;
/** A sparkle on the glass this often. */
const SPARKLE_MS = 350;

/**
 * A hedge maze match's prize (owner, 2026-10-29): the diamond crystal star (the rare finds' own
 * sprite) floating over the middle tile, bobbing up and down and glinting, with its shadow on the
 * paving below. The first mower to touch it (onto the paving beside it: the plaza is the finish)
 * wins, and the star flies to the winner. Drawn only; the sim knows nothing of it.
 */
export class MazeStar {
  private readonly star: Phaser.GameObjects.Image;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly sparkle: Phaser.GameObjects.Graphics;
  private readonly x: number;
  private readonly y: number;
  /** Once won: where the star has flown (it bobs over the winner's mower from then on). */
  private won: (() => { x: number; y: number }) | null = null;
  private nextSparkle = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    tileX: number,
    tileY: number,
    depth: number,
  ) {
    this.x = (tileX + 0.5) * TILE_SIZE;
    this.y = (tileY + 0.5) * TILE_SIZE;
    this.shadow = scene.add.ellipse(this.x, this.y + 5, 10, 4, 0x000000, 0.3).setDepth(depth - 2);
    this.star = scene.add
      .image(this.x, this.y - FLOAT, PICKUPS_KEY, PICKUP_FRAME.rare)
      .setOrigin(0.5, 1)
      .setDepth(depth);
    this.sparkle = scene.add.graphics().setDepth(depth + 1);
  }

  /** Bobs, glints, and (once won) follows the winner. */
  update(now: number): void {
    const lift = FLOAT + BOB * Math.sin((now / BOB_MS) * Math.PI * 2);
    const at = this.won?.() ?? { x: this.x, y: this.y };
    this.star.setPosition(Math.round(at.x), Math.round(at.y + 4 - lift));
    this.shadow
      .setPosition(at.x, at.y + 5)
      .setScale(1 - (lift - FLOAT) * 0.08)
      .setVisible(!this.won);
    if (now < this.nextSparkle) return;
    this.nextSparkle = now + SPARKLE_MS;
    // A glint somewhere on the crystal: a small white cross.
    const g = this.sparkle.clear();
    const sx = this.star.x - 5 + Math.floor(Math.random() * 10);
    const sy = this.star.y - 13 + Math.floor(Math.random() * 9);
    g.fillStyle(0xffffff, 1)
      .fillRect(sx, sy - 1, 1, 3)
      .fillRect(sx - 1, sy, 3, 1);
    this.scene.time.delayedCall(140, () => this.sparkle.clear());
  }

  /** Someone touched it: it flies to them (`where`: their mower's position each frame). */
  winTo(where: () => { x: number; y: number }): void {
    if (this.won) return;
    const from = { x: this.x, y: this.y };
    const start = this.scene.time.now;
    const FLY_MS = 450;
    this.won = () => {
      const t = Math.min(1, (this.scene.time.now - start) / FLY_MS);
      const to = where();
      const hop = Math.sin(t * Math.PI) * 14;
      return {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - 6 - from.y) * t - hop,
      };
    };
  }
}
